import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import DynamicTable from "src/components/table";
import { columntype, isValidArray } from "src/helper/publicFunction";
import {
  GetAllWebhookLocks,
  GetWebhookLocksByOrderId,
  GetDuplicateOrders,
  GetDuplicateCourses,
  GetDiagnosticByOrderId,
  GetClientOrdersAdmin,
} from "./actions";

const SECTION_KEYS = {
  allLocks: "allLocks",
  duplicateOrders: "duplicateOrders",
  duplicateCourses: "duplicateCourses",
  clientOrders: "clientOrders",
  orderLocks: "orderLocks",
};

const ensureArray = (payload) => {
  if (isValidArray(payload?.data)) return payload.data;
  if (isValidArray(payload)) return payload;
  return [];
};

const normalizeRowsWithId = (rows, idBuilder) =>
  ensureArray(rows).map((item, index) => ({
    ...item,
    id:
      item?.id ??
      (typeof idBuilder === "function" ? idBuilder(item, index) : `row-${index}`),
  }));

const firstNonEmptyArray = (...candidates) =>
  candidates.find((arr) => isValidArray(arr)) ?? [];

const WebhookLocks = () => {
  const dispatch = useDispatch();
  const [activeSection, setActiveSection] = useState(SECTION_KEYS.allLocks);
  const [orderId, setOrderId] = useState("");
  const [loadingData, setLoadingData] = useState(false);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [search, setSearch] = useState("");

  const [allLocks, setAllLocks] = useState([]);
  const [duplicateOrders, setDuplicateOrders] = useState([]);
  const [duplicateCourses, setDuplicateCourses] = useState([]);
  const [orderLocks, setOrderLocks] = useState([]);
  const [orderLocksDetails, setOrderLocksDetails] = useState(null);
  const [diagnosticResult, setDiagnosticResult] = useState(null);
  const [clientOrders, setClientOrders] = useState([]);
  const [clientOrdersTotal, setClientOrdersTotal] = useState(0);
  const [clientOrdersLoaded, setClientOrdersLoaded] = useState(false);

  const fetchAllLocks = () => {
    setLoadingData(true);
    dispatch(
      GetAllWebhookLocks({
        callback: (response) => {
          setAllLocks(normalizeRowsWithId(response, (_, index) => `lock-${index}`));
          setLoadingData(false);
        },
      })
    );
  };

  const fetchDuplicateOrders = () => {
    setLoadingData(true);
    dispatch(
      GetDuplicateOrders({
        callback: (response) => {
          setDuplicateOrders(
            normalizeRowsWithId(
              response,
              (item, index) => `dup-order-${item?.order_id ?? index}`
            )
          );
          setLoadingData(false);
        },
      })
    );
  };

  const fetchDuplicateCourses = () => {
    setLoadingData(true);
    dispatch(
      GetDuplicateCourses({
        callback: (response) => {
          setDuplicateCourses(
            normalizeRowsWithId(
              response,
              (item, index) =>
                `dup-course-${item?.client_id ?? "c"}-${item?.course_id ?? "k"}-${index}`
            )
          );
          setLoadingData(false);
        },
      })
    );
  };

  const fetchClientOrders = () => {
    setLoadingData(true);
    dispatch(
      GetClientOrdersAdmin({
        page: page + 1,
        rowsPerPage,
        search,
        callback: (response) => {
          if (isValidArray(response?.data)) {
            setClientOrders(
              normalizeRowsWithId(response.data, (_, index) => `client-order-${index}`)
            );
            setClientOrdersTotal(response?.count ?? response.data.length);
          } else {
            setClientOrders([]);
            setClientOrdersTotal(0);
          }
          setClientOrdersLoaded(true);
          setLoadingData(false);
        },
      })
    );
  };

  useEffect(() => {
    fetchAllLocks();
    fetchDuplicateOrders();
    fetchDuplicateCourses();
  }, []);

  useEffect(() => {
    if (activeSection === SECTION_KEYS.clientOrders) {
      fetchClientOrders();
    }
  }, [activeSection, page, rowsPerPage, search]);

  const handleFetchByOrder = () => {
    if (!orderId) return;
    setLoadingData(true);
    dispatch(
      GetWebhookLocksByOrderId({
        orderId,
        callback: (response) => {
          const payload = response?.data ?? response ?? {};
          setOrderLocks(
            normalizeRowsWithId(
              firstNonEmptyArray(
                ensureArray(payload?.locks),
                ensureArray(payload?.webhook_locks),
                ensureArray(payload)
              ),
              (_, index) => `order-lock-${index}`
            )
          );
          setOrderLocksDetails(payload);
          setActiveSection(SECTION_KEYS.orderLocks);
          setLoadingData(false);
        },
      })
    );
  };

  const handleDiagnostic = () => {
    if (!orderId) return;
    setLoadingData(true);
    dispatch(
      GetDiagnosticByOrderId({
        orderId,
        callback: (response) => {
          setDiagnosticResult(response?.data ?? response ?? null);
          setLoadingData(false);
        },
      })
    );
  };

  const sections = [
    { key: SECTION_KEYS.allLocks, label: "All Locks" },
    { key: SECTION_KEYS.duplicateOrders, label: "Duplicate Orders" },
    { key: SECTION_KEYS.duplicateCourses, label: "Duplicate Courses" },
    { key: SECTION_KEYS.clientOrders, label: "Client Orders (Admin)" },
    { key: SECTION_KEYS.orderLocks, label: "Locks By Order" },
  ];

  const columnsBySection = {
    [SECTION_KEYS.allLocks]: [
      { type: columntype.text, name: "id" },
      { type: columntype.text, name: "order_id" },
      { type: columntype.text, name: "client_id" },
      { type: columntype.text, name: "course_id" },
      { type: columntype.dateAndtime, name: "createdAt" },
      { type: columntype.dateAndtime, name: "updatedAt" },
    ],
    [SECTION_KEYS.duplicateOrders]: [
      { type: columntype.text, name: "order_id" },
      { type: columntype.text, name: "count" },
      { type: columntype.dateAndtime, name: "latest_createdAt" },
    ],
    [SECTION_KEYS.duplicateCourses]: [
      { type: columntype.text, name: "client_id" },
      { type: columntype.text, name: "course_id" },
      { type: columntype.text, name: "count" },
    ],
    [SECTION_KEYS.clientOrders]: [
      { type: columntype.text, name: "id" },
      { type: columntype.text, name: "client_name" },
      { type: columntype.text, name: "client_email" },
      { type: columntype.text, name: "order_price" },
      { type: columntype.text, name: "payment_status" },
      { type: columntype.text, name: "course_id" },
    ],
    [SECTION_KEYS.orderLocks]: [
      { type: columntype.text, name: "id" },
      { type: columntype.text, name: "order_id" },
      { type: columntype.text, name: "client_id" },
      { type: columntype.text, name: "course_id" },
      { type: columntype.dateAndtime, name: "createdAt" },
      { type: columntype.dateAndtime, name: "updatedAt" },
    ],
  };

  const headCellsBySection = {
    [SECTION_KEYS.allLocks]: [
      { id: "id", numeric: false, disablePadding: false, label: "ID", withSort: false },
      { id: "order_id", numeric: false, disablePadding: false, label: "Order ID", withSort: false },
      { id: "client_id", numeric: false, disablePadding: false, label: "Client ID", withSort: false },
      { id: "course_id", numeric: false, disablePadding: false, label: "Course ID", withSort: false },
      { id: "createdAt", numeric: false, disablePadding: false, label: "Created", withSort: false },
      { id: "updatedAt", numeric: false, disablePadding: false, label: "Updated", withSort: false },
    ],
    [SECTION_KEYS.duplicateOrders]: [
      { id: "order_id", numeric: false, disablePadding: false, label: "Order ID", withSort: false },
      { id: "count", numeric: false, disablePadding: false, label: "Count", withSort: false },
      { id: "latest_createdAt", numeric: false, disablePadding: false, label: "Latest Created", withSort: false },
    ],
    [SECTION_KEYS.duplicateCourses]: [
      { id: "client_id", numeric: false, disablePadding: false, label: "Client ID", withSort: false },
      { id: "course_id", numeric: false, disablePadding: false, label: "Course ID", withSort: false },
      { id: "count", numeric: false, disablePadding: false, label: "Count", withSort: false },
    ],
    [SECTION_KEYS.clientOrders]: [
      { id: "id", numeric: false, disablePadding: false, label: "ID", withSort: false },
      { id: "client_name", numeric: false, disablePadding: false, label: "Client Name", withSort: false },
      { id: "client_email", numeric: false, disablePadding: false, label: "Client Email", withSort: false },
      { id: "order_price", numeric: false, disablePadding: false, label: "Order Price", withSort: false },
      { id: "payment_status", numeric: false, disablePadding: false, label: "Payment Status", withSort: false },
      { id: "course_id", numeric: false, disablePadding: false, label: "Course ID", withSort: false },
    ],
    [SECTION_KEYS.orderLocks]: [
      { id: "id", numeric: false, disablePadding: false, label: "ID", withSort: false },
      { id: "order_id", numeric: false, disablePadding: false, label: "Order ID", withSort: false },
      { id: "client_id", numeric: false, disablePadding: false, label: "Client ID", withSort: false },
      { id: "course_id", numeric: false, disablePadding: false, label: "Course ID", withSort: false },
      { id: "createdAt", numeric: false, disablePadding: false, label: "Created", withSort: false },
      { id: "updatedAt", numeric: false, disablePadding: false, label: "Updated", withSort: false },
    ],
  };

  const tableData = useMemo(() => {
    if (activeSection === SECTION_KEYS.duplicateOrders) return duplicateOrders;
    if (activeSection === SECTION_KEYS.duplicateCourses) return duplicateCourses;
    if (activeSection === SECTION_KEYS.clientOrders) return clientOrders;
    if (activeSection === SECTION_KEYS.orderLocks) return orderLocks;
    return allLocks;
  }, [activeSection, allLocks, duplicateOrders, duplicateCourses, clientOrders, orderLocks]);

  const tableTotal =
    activeSection === SECTION_KEYS.clientOrders ? clientOrdersTotal : tableData.length;

  return (
    <>
      <Paper sx={{ p: 2, mx: 2, mb: 2 }} variant="outlined">
        <Stack spacing={2}>
          <Typography variant="h5">Webhook Locks Diagnostics</Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              size="small"
              label="Order ID"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
            />
            <Button variant="contained" onClick={handleFetchByOrder}>
              Locks by Order
            </Button>
            <Button variant="outlined" onClick={handleDiagnostic}>
              Full Diagnostic
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {sections.map((section) => (
              <Button
                key={section.key}
                size="small"
                variant={activeSection === section.key ? "contained" : "outlined"}
                onClick={() => {
                  setActiveSection(section.key);
                  setSelected([]);
                  if (
                    section.key === SECTION_KEYS.clientOrders &&
                    !clientOrdersLoaded
                  ) {
                    setPage(0);
                  }
                }}
              >
                {section.label}
              </Button>
            ))}
          </Stack>
        </Stack>
      </Paper>

      <DynamicTable
        handleSearch={(event) => setSearch(event.target.value)}
        onRequestSort={() => {}}
        onPressDelete={() => {}}
        onPressItem={() => {}}
        onPressAnyAction={() => {}}
        onPressAdd={() => {}}
        items={tableData}
        columns={columnsBySection[activeSection]}
        headCells={headCellsBySection[activeSection]}
        page={page}
        setPage={setPage}
        selected={selected}
        setSelected={setSelected}
        search={search}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        Listdropdown={[]}
        withOption={false}
        loadingData={loadingData}
        rowsPerPageOptions={[5, 10, 25, 50]}
        totalItem={tableTotal}
        loadingEditdatatype=""
        withAddButton={false}
        withDeleteItem={false}
        withEditItem={false}
        withPagination={activeSection === SECTION_KEYS.clientOrders}
        withSearch={activeSection === SECTION_KEYS.clientOrders}
      />

      {activeSection === SECTION_KEYS.orderLocks && orderLocksDetails ? (
        <Paper sx={{ p: 2, mx: 2, mt: 2 }} variant="outlined">
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Locks By Order Details
          </Typography>
          <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
            {JSON.stringify(orderLocksDetails, null, 2)}
          </pre>
        </Paper>
      ) : null}

      {diagnosticResult ? (
        <Paper sx={{ p: 2, mx: 2, mt: 2 }} variant="outlined">
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Full Diagnostic Result
          </Typography>
          <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
            {JSON.stringify(diagnosticResult, null, 2)}
          </pre>
        </Paper>
      ) : null}
    </>
  );
};

export default WebhookLocks;
